import { useState, useEffect, useCallback, useRef } from "react";
import { ReactSVG } from "react-svg";
// Import SVG as a URL
import MapSVG from "./interactive-map.svg";

// Region coordinates for form integration - using same fixed address for all regions
export const regionCoordinates = {
  "City of Toronto": {
    postalCode: "M5H 2N3",
    address: "100 Queen St W, Toronto, ON, Canada",
  },
  Vaughan: {
    postalCode: "L6A 1T1",
    address: "2141 Major MacKenzie Dr W, Vaughan, ON, Canada",
  },
  Newmarket: {
    postalCode: "L3Y 4X7",
    address: "395 Mulock Dr, Newmarket, ON, Canada",
  },
  Aurora: {
    postalCode: "L4G 6J1",
    address: "Aurora Town Hall, 100 John West Way, Aurora, ON, Canada",
  },
  "Richmond Hill": {
    postalCode: "L4B 3P4",
    address: "225 East Beaver Creek Rd, Richmond Hill, ON, Canada",
  },
  "Whitchurch-stouffville": {
    postalCode: "L4A 0Z9",
    address: "111 Sandiford Dr, Whitchurch-Stouffville, ON, Canada",
  },
  Markham: {
    postalCode: "L3R 9W3",
    address: "Markham Civic Centre, 101 Town Centre Blvd, Markham, ON, Canada",
  },
  Pickering: {
    postalCode: "L1V 6K7",
    address: "1 The Esplanade S, Pickering, ON, Canada",
  },
  Ajax: {
    postalCode: "L1S 2H9",
    address: "65 Harwood Ave S, Ajax, ON, Canada",
  },
  Mississauga: {
    postalCode: "L5B 3C1",
    address: "300 City Centre Dr, Mississauga, ON, Canada",
  },
  Oakville: {
    postalCode: "L6H 0H3",
    address: "1225 Trafalgar Rd, Oakville, ON, Canada",
  },
};

export interface InteractiveMapProps {
  onLocationSelect: (from: string | null, to: string | null) => void;
}

export function InteractiveMap({ onLocationSelect }: InteractiveMapProps) {
  const [selections, setSelections] = useState<{
    from: string | null;
    to: string | null;
  }>({ from: null, to: null });
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Using a single state object to track both selections helps prevent race conditions
  const handleRegionClick = useCallback((id: string) => {
    setSelections((prev) => {
      // If no "from" selected yet, set as "from"
      if (!prev.from) {
        return { ...prev, from: id };
      }
      // If "from" selected but no "to", set as "to"
      else if (!prev.to) {
        // Don't allow same location for both from and to
        if (id !== prev.from) {
          return { ...prev, to: id };
        }
      }
      // If both selected, reset and set as new "from"
      else {
        return { from: id, to: null };
      }

      // Return unchanged state if none of the conditions matched
      return prev;
    });
  }, []);

  // Update SVG colors when selections change
  useEffect(() => {
    if (!svgRef.current) return;

    const svg = svgRef.current;
    // Get the main region (g) elements
    const regions = Array.from(svg.querySelectorAll("g#Map > g"));

    regions.forEach((region) => {
      const id = region.getAttribute("id");
      if (!id) return;

      // Find the first path in each region (the main shape)
      const mainPath = region.querySelector("path");
      if (!mainPath) return;

      if (selections.from === id) {
        mainPath.setAttribute("fill", "#9333EA"); // Purple for "from"
      } else if (selections.to === id) {
        mainPath.setAttribute("fill", "#EC4899"); // Pink for "to"
      } else {
        // Reset to original color - this requires knowing original colors
        const originalColors: Record<string, string> = {
          "City of Toronto": "#2DEEFF",
          Vaughan: "#495BB6",
          Newmarket: "#366ADC",
          Aurora: "#2DB9FF",
          "Richmond Hill": "#0073FF",
          "Whitchurch-stouffville": "#2DEEFF",
          Markham: "#1B6F99",
          Pickering: "#495BB6",
          Ajax: "#366ADC",
          Mississauga: "#2DB9FF",
          Oakville: "#2DEEFF",
        };
        mainPath.setAttribute("fill", originalColors[id] || "#ccc");
      }
    });
  }, [selections]);

  // Notify parent about selection changes
  useEffect(() => {
    onLocationSelect(selections.from, selections.to);
  }, [selections, onLocationSelect]);

  const handleSvgLoad = (svg: SVGSVGElement) => {
    // Store reference to svg
    svgRef.current = svg;

    // Ensure SVG is responsive and visible
    svg.setAttribute("width", "100%");
    svg.setAttribute("height", "100%");
    svg.setAttribute("preserveAspectRatio", "xMidYMid meet");

    // Find all main region groups and add click handlers
    const regions = Array.from(svg.querySelectorAll("g#Map > g"));
    regions.forEach((region) => {
      const id = region.getAttribute("id");
      if (!id) return;

      // Find the main path (region shape)
      const mainPath = region.querySelector("path");
      if (!mainPath) return;

      // Add hover effect with CSS
      mainPath.classList.add(
        "cursor-pointer",
        "hover:opacity-80",
        "transition-all"
      );

      // Add click handler
      mainPath.addEventListener("click", () => handleRegionClick(id));
    });

    setIsLoading(false);
  };

  return (
    <div className="relative h-[360px] sm:h-[400px] md:h-full min-h-[300px]">
      <div className="h-full w-full relative flex items-center justify-center">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50/50 backdrop-blur-sm z-10">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-purple-600 animate-pulse"></div>
              <div className="w-4 h-4 rounded-full bg-pink-600 animate-pulse delay-150"></div>
              <div className="w-4 h-4 rounded-full bg-blue-600 animate-pulse delay-300"></div>
            </div>
          </div>
        )}
        <ReactSVG
          src={MapSVG}
          beforeInjection={handleSvgLoad}
          afterInjection={(svg: SVGSVGElement) => {
            // Initial color setup based on current selections
            const regions = Array.from(svg.querySelectorAll("g#Map > g"));
            regions.forEach((region) => {
              const id = region.getAttribute("id");
              if (!id) return;

              // Find the main path (region shape)
              const mainPath = region.querySelector("path");
              if (!mainPath) return;

              if (selections.from === id) {
                mainPath.setAttribute("fill", "#9333EA");
              } else if (selections.to === id) {
                mainPath.setAttribute("fill", "#EC4899");
              }
            });
          }}
          className="h-full w-full"
          fallback={() => (
            <div className="flex items-center justify-center h-full">
              Error loading map
            </div>
          )}
          wrapper="div"
          evalScripts="always"
          renumerateIRIElements={true}
          style={{ height: "100%", width: "100%" }}
          // @ts-expect-error - Adding inline styles to ensure SVG rendering
          svgStyle={{
            height: "100%",
            width: "100%",
            display: "block",
            maxWidth: "100%",
          }}
          svgClassName="interactive-map-svg"
        />
      </div>
    </div>
  );
}
